import type { AdminPickerListItemDto } from '@carcheck/shared';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';

import { tokens } from '../../../design';
import { formatDate } from '../../../utils/format';
import { initials } from '../../../utils/initials';
import { placeText } from '../../../utils/place';
import { shortId } from '../../inspectors/_lib/displayName';
import { PickerSourceBadge, PickerStateBadge } from '../_components/PickerBadges';
import { PickerCompany } from '../_components/PickerCompany';
import { PiiText } from '../_components/PiiText';

/**
 * Columns for the pickers grid (AUT-400), after `pickers-list.html`:
 * Подборщик / Телефон / Город / Источник / Компания / Отчётов / Активных
 * ссылок / Создан / Статус. Sorting is off — the server order is fixed
 * (newest grant first).
 *
 * Компания is the active membership: name + «владелец» / «сотрудник», «—»
 * for a solo picker. For a disabled picker the link count reads «ссылки
 * закрыты» — its links are dark whatever the number says (the async sweep
 * may not have landed yet, and the public read 404s regardless).
 */

const Muted = ({ children }: { children: string }): ReactElement => (
  <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
    {children}
  </Typography>
);

const PickerCell = ({ row, t }: { row: AdminPickerListItemDto; t: TFunction }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%' }}>
    <Avatar
      sx={{
        width: 30,
        height: 30,
        fontSize: 11,
        fontWeight: 700,
        ...(row.disabled !== null ? { opacity: 0.5 } : {}),
      }}
    >
      {initials(row.name)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} noWrap>
        {row.name ?? t('pickers.noValue')}
      </Typography>
      <Typography variant="caption" sx={{ color: tokens.text.tertiary }} fontFamily="monospace">
        {shortId(row.userId)}
      </Typography>
    </Box>
  </Box>
);

const num = (value: number): ReactElement => (
  <Typography variant="body2" fontFamily="monospace">
    {value}
  </Typography>
);

type Col = GridColDef<AdminPickerListItemDto>;

const whoColumns = (t: TFunction): Col[] => [
  {
    field: 'picker',
    headerName: t('pickers.columns.picker'),
    flex: 1.4,
    minWidth: 210,
    sortable: false,
    renderCell: ({ row }) => <PickerCell row={row} t={t} />,
  },
  {
    field: 'phone',
    headerName: t('pickers.columns.phone'),
    flex: 1,
    minWidth: 160,
    sortable: false,
    renderCell: ({ row }) => <PiiText value={row.phone} />,
  },
  {
    field: 'city',
    headerName: t('pickers.columns.city'),
    flex: 0.7,
    minWidth: 100,
    sortable: false,
    renderCell: ({ row }) => {
      const place = placeText(row);
      return place ? <Typography variant="body2">{place}</Typography> : <Muted>—</Muted>;
    },
  },
  {
    field: 'source',
    headerName: t('pickers.columns.source'),
    flex: 0.6,
    minWidth: 100,
    sortable: false,
    renderCell: ({ row }) => <PickerSourceBadge source={row.source} />,
  },
  {
    field: 'company',
    headerName: t('pickers.columns.company'),
    flex: 0.8,
    minWidth: 160,
    sortable: false,
    renderCell: ({ row }) => <PickerCompany company={row.company} />,
  },
];

const activityColumns = (t: TFunction): Col[] => [
  {
    field: 'reportCount',
    headerName: t('pickers.columns.reports'),
    flex: 0.5,
    minWidth: 90,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell: ({ row }) => num(row.reportCount),
  },
  {
    field: 'activeLinkCount',
    headerName: t('pickers.columns.activeLinks'),
    flex: 0.6,
    minWidth: 120,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell: ({ row }) =>
      row.disabled !== null ? <Muted>{t('pickers.linksClosed')}</Muted> : num(row.activeLinkCount),
  },
  {
    field: 'createdAt',
    headerName: t('pickers.columns.createdAt'),
    flex: 0.6,
    minWidth: 100,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" fontFamily="monospace">
        {formatDate(row.createdAt)}
      </Typography>
    ),
  },
  {
    field: 'state',
    headerName: t('pickers.columns.state'),
    flex: 0.6,
    minWidth: 110,
    sortable: false,
    renderCell: ({ row }) => <PickerStateBadge disabled={row.disabled !== null} />,
  },
];

export const buildPickerColumns = (t: TFunction): Col[] => [
  ...whoColumns(t),
  ...activityColumns(t),
];
